import type { DecimalString } from '../types/model-configuration.types';

/**
 * Renders a per-1M-token price.
 *
 * Takes `string | number` because Pydantic serialises `Decimal` as a JSON
 * string: parsing it to a number here is safe for display (these are small
 * dollar figures, nowhere near float precision limits) but the raw value is
 * never sent back, so no rounding ever reaches the server.
 */
export function formatCostPerMillion(value: DecimalString): string {
  const parsed = typeof value === 'number' ? value : Number.parseFloat(value);
  if (!Number.isFinite(parsed)) return '—';
  if (parsed === 0) return '$0.00';
  // Four decimals: the cheap models are priced in fractions of a cent per 1M,
  // and two decimals would render several of them as $0.00.
  return `$${parsed.toFixed(parsed < 0.01 ? 4 : 2)}`;
}

/** True when a catalog row still carries the placeholder price. */
export function isPlaceholderPricing(pricingSource: string): boolean {
  return pricingSource === 'PLACEHOLDER' || pricingSource === '';
}

/**
 * Compact token count for limits and ceilings — `128k`, `16k`, `1M`.
 *
 * Exact powers-of-two multiples of 1,024 (16,384, 131,072) render as the `k`
 * figure vendors quote them as; anything else rounds to one decimal. Zero means
 * "not specified" on the wire, so it renders as an em dash rather than `0`.
 */
export function formatTokenCount(value: number): string {
  if (!Number.isFinite(value) || value <= 0) return '—';
  if (value >= 1_000_000 && value % 1_048_576 === 0) return `${String(value / 1_048_576)}M`;
  if (value >= 1_000_000) return `${trimDecimal(value / 1_000_000)}M`;
  if (value >= 1_024 && value % 1_024 === 0) return `${String(value / 1_024)}k`;
  if (value >= 1_000) return `${trimDecimal(value / 1_000)}k`;
  return String(value);
}

/** One decimal place, with a trailing `.0` dropped. */
function trimDecimal(value: number): string {
  const rounded = Math.round(value * 10) / 10;
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
}
